import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Glassmorphism Header with Silver Metallic Background */}
      <header className="sticky top-0 left-0 right-0 z-50 border-b border-white/40 shadow-sm">
        {/* Silver metallic gradient background layer */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/60 via-slate-200/60 to-slate-300/60" 
             style={{
               backgroundImage: 'linear-gradient(135deg, rgba(226, 232, 240, 0.6) 0%, rgba(241, 245, 249, 0.6) 25%, rgba(226, 232, 240, 0.6) 50%, rgba(241, 245, 249, 0.6) 75%, rgba(226, 232, 240, 0.6) 100%)',
               backdropFilter: 'blur(20px)',
               WebkitBackdropFilter: 'blur(20px)'
             }}>
        </div>
        
        {/* Content layer */}
        <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_circuitwizard-1/artifacts/rg1a98x7_eztechguys%20logonew.avif"
                alt="Apple Tech Guys Logo"
                className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-110"
              />
              <div>
                <div className="text-slate-900 font-bold text-base md:text-lg leading-tight">
                  Apple Tech Guy's
                </div>
                <div className="text-sky-600 text-xs md:text-sm font-semibold">
                  Electronics Repair
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-base font-semibold transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-sky-600'
                      : 'text-slate-600 hover:text-sky-500'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              <Button
                asChild
                className="rounded-full bg-sky-500 text-white px-6 py-2 font-semibold hover:bg-sky-600 transition-all hover:scale-[1.02] shadow-md"
              >
                <a href="tel:+19782576427">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-900 p-2 hover:text-sky-600 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <div className="flex flex-col gap-4">
                {navigation.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-base font-semibold transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'text-sky-600'
                        : 'text-slate-600 hover:text-sky-500'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                
                <Button
                  asChild
                  className="rounded-full bg-sky-500 text-white px-6 py-3 font-semibold hover:bg-sky-600 w-full"
                >
                  <a href="tel:+19782576427">
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </a>
                </Button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer with warm tan accent */}
      <footer className="bg-gradient-to-br from-slate-100 to-stone-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_circuitwizard-1/artifacts/rg1a98x7_eztechguys%20logonew.avif"
                  alt="Apple Tech Guys Logo"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <div className="text-slate-900 font-bold text-lg leading-tight">
                    Apple Tech Guy's
                  </div>
                  <div className="text-sky-600 text-sm font-semibold">
                    Electronics Repair
                  </div>
                </div>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                New Hampshire's most trusted electronics repair service. Expert repairs, same-day service, lifetime warranty.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-slate-900 font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-slate-600 hover:text-sky-600 transition-colors text-sm"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-slate-900 font-bold text-lg mb-4">Contact Us</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="tel:+19782576427" className="text-slate-600 hover:text-sky-600 transition-colors font-medium">
                    Phone: (978) 257-6427
                  </a>
                </li>
                <li>
                  <a href="mailto:kyleperkinstech@gmail.com" className="text-slate-600 hover:text-sky-600 transition-colors break-all">
                    kyleperkinstech@gmail.com
                  </a>
                </li>
                <li className="text-slate-600">
                  22 Hammond Street<br />
                  Concord, NH 03301
                </li>
                <li className="text-slate-600 pt-3 border-t border-slate-300">
                  <span className="text-sky-600 font-semibold">Hours:</span><br />
                  Mon-Sat: 11am - 10pm<br />
                  Sun: By Appointment
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-slate-300">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-500 text-sm">
                © {new Date().getFullYear()} Apple Tech Guy's Electronics Repair. All rights reserved.
                {' • '}
                <a href="/ai-summary.html" className="text-slate-600 hover:text-sky-600 transition-colors underline">
                  Business Summary
                </a>
              </p>
              <p className="text-sky-600 font-semibold text-sm">
                Same Day Repairs • Lifetime Warranty • Expert Service
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
